import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const COUNTRIES = [
  'Uganda', 'Kenya', 'Tanzania', 'Rwanda', 'Burundi',
  'Nigeria', 'Ghana', 'South Africa', 'Zambia', 'Malawi',
  'Cameroon', 'Senegal', 'Ivory Coast',
  'United States', 'United Kingdom', 'Canada',
  'France', 'Germany', 'Netherlands', 'Belgium', 'Italy', 'Spain',
  'Other',
];

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const profileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [form, setForm] = useState({ name: '', business_name: '', phone: '', country: '', default_currency: '', fee_payer: 'merchant', public_support_email: '', public_support_phone: '', website_url: '' });
  const [saving, setSaving] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [profileUrl, setProfileUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get('/profile').then(r => {
      const d = r.data || {};
      setFeatureEnabled(d.fee_payer_feature_enabled !== false);
      setForm({
        name: d.name || '',
        business_name: d.business_name || '',
        phone: d.phone || '',
        country: d.country || 'Uganda',
        default_currency: d.default_currency || 'UGX',
        fee_payer: d.fee_payer || 'merchant',
        public_support_email: d.public_support_email || '',
        public_support_phone: d.public_support_phone || '',
        website_url: d.website_url || '',
      });
      setProfileUrl(d.profile_photo_url || '');
      setLogoUrl(d.merchant_logo_url || '');
    }).catch(() => {});
  }, []);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const uploadImage = async (file, kind) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image must be smaller than 3MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('kind', kind);
      const { data } = await api.post('/profile/media', fd);
      if (data.field === 'profile_photo_url') setProfileUrl(data.url);
      if (data.field === 'merchant_logo_url') setLogoUrl(data.url);
      toast.success('Image uploaded');
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/profile', form);
      toast.success('Profile updated!');
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Profile Settings</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage your account information</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-lg font-semibold text-slate-900">{user?.name}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user?.account_type === 'business' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}>
                  {user?.account_type || 'individual'}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user?.account_status === 'approved' ? 'bg-emerald-100 text-emerald-800' : user?.account_status === 'suspended' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                  {user?.account_status || user?.role}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input data-testid="profile-name-input" value={form.name} onChange={e => update('name', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Business Name</Label>
                <Input data-testid="profile-business-input" value={form.business_name} onChange={e => update('business_name', e.target.value)} className="mt-1" placeholder="Optional" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="profile-phone-input" value={form.phone} onChange={e => update('phone', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Public Support Email</Label>
                <Input value={form.public_support_email} onChange={e => update('public_support_email', e.target.value)} className="mt-1" placeholder="support@yourbrand.com" />
              </div>
              <div>
                <Label>Public Support Phone</Label>
                <Input value={form.public_support_phone} onChange={e => update('public_support_phone', e.target.value)} className="mt-1" placeholder="+256..." />
              </div>
              <div>
                <Label>Country</Label>
                <Select value={form.country} onValueChange={v => update('country', v)}>
                  <SelectTrigger className="mt-1" data-testid="profile-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default Currency</Label>
                <Select value={form.default_currency} onValueChange={v => update('default_currency', v)}>
                  <SelectTrigger className="mt-1" data-testid="profile-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UGX">UGX - Ugandan Shilling</SelectItem>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="KES">KES - Kenyan Shilling</SelectItem>
                    <SelectItem value="TZS">TZS - Tanzanian Shilling</SelectItem>
                    <SelectItem value="RWF">RWF - Rwandan Franc</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="NGN">NGN - Nigerian Naira</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Website or Social Link</Label>
                <Input value={form.website_url} onChange={e => update('website_url', e.target.value)} className="mt-1" placeholder="https://..." />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
                <Label className="text-sm font-medium text-slate-700">Profile Photo</Label>
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-white border border-slate-200 flex items-center justify-center">
                    {profileUrl ? <img src={profileUrl} alt="Profile" className="w-full h-full object-cover" /> : <span className="text-slate-400 text-xs">No photo</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => profileInputRef.current?.click()} disabled={uploading}>Upload</Button>
                    <input ref={profileInputRef} type="file" accept="image/*" className="hidden" onChange={e => uploadImage(e.target.files?.[0], 'profile_photo')} />
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
                <Label className="text-sm font-medium text-slate-700">Business or Personal Logo</Label>
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-20 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 flex items-center justify-center">
                    {logoUrl ? <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" /> : <span className="text-slate-400 text-xs">No logo</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploading}>Upload</Button>
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={e => uploadImage(e.target.files?.[0], 'merchant_logo')} />
                  </div>
                </div>
              </div>
            </div>

            {featureEnabled && (
              <div className="pt-4 border-t border-slate-100">
                <Label>Who pays the transaction fee?</Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">Choose who absorbs the Jjuma Pay fee on every payment.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { value: 'merchant', label: 'Me (the receiver/merchant)', help: 'Customer pays the exact amount. Fee is deducted from your received amount.' },
                    { value: 'customer', label: 'The customer/payer', help: 'Customer pays amount + fee. You receive the full intended amount.' },
                  ].map(opt => (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => update('fee_payer', opt.value)}
                      data-testid={`fee-payer-${opt.value}`}
                      className={`text-left p-3 rounded-xl border-2 transition-colors ${form.fee_payer === opt.value ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    >
                      <p className={`text-sm font-medium ${form.fee_payer === opt.value ? 'text-emerald-700' : 'text-slate-700'}`}>{opt.label}</p>
                      <p className="text-xs text-slate-500 mt-1">{opt.help}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Button type="submit" data-testid="save-profile-btn" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
